import { ErrorBoundary } from 'react-error-boundary';
import { Box, Button, Typography, Container, Paper } from '@mui/material';
import { Refresh, Home, Agriculture } from '@mui/icons-material';
import wiltingPlant from '../../static_assets/wilting_plant.gif';

// Error logging function
async function logErrorToServer(error, info) {
    try {
        console.error("=== APPLICATION ERROR ===");
        console.error("Error:", error.message);
        console.error("Stack:", error.stack);
        console.error("Component Stack:", info?.componentStack);
        console.error("URL:", window.location.href);
        console.error("Timestamp:", new Date().toISOString());
        console.error("========================");

        // Store error in localStorage for debugging
        try {
            const errorLog = {
                message: error.message,
                name: error.name,
                stack: error.stack,
                componentStack: info?.componentStack,
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href,
            };
            localStorage.setItem('lastError', JSON.stringify(errorLog));
        } catch (storageError) {
            console.error('Failed to store error in localStorage:', storageError);
        }

        // Uncomment when you have an error logging endpoint
        // await fetch('/api/log-client-error', {
        //   method: 'POST',
        //   headers: {
        //     'Content-Type': 'application/json',
        //   },
        //   body: JSON.stringify(errorLog),
        // });
    } catch (e) {
        console.error('Failed to log error:', e);
    }
}

// User-friendly error fallback UI with FarmSmarter theme
function ErrorFallback({ error, resetErrorBoundary }) {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(180deg, #0E8BEF 20.31%, #718A33 81.77%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
            }}
        >
            <Container maxWidth="md">
                <Paper
                    elevation={8}
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        bgcolor: 'rgba(255, 255, 255, 0.98)',
                    }}
                >
                    {/* Header Section with Brand Colors */}
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #003C37 0%, #14504B 100%)',
                            padding: 4,
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Decorative Background Pattern */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                opacity: 0.1,
                                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.1) 10px, rgba(255,255,255,.1) 20px)',
                            }}
                        />

                        {/* Icon */}
                        <Box
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                mb: 2,
                            }}
                        >
                            <img
                                src={wiltingPlant}
                                alt="Error"
                                style={{
                                    width: '120px',
                                    height: '120px',
                                    filter: 'drop-shadow(0px 4px 8px rgba(0,0,0,0.2))',
                                }}
                            />
                        </Box>

                        <Typography
                            variant="h3"
                            component="h1"
                            sx={{
                                color: '#D5EE97',
                                fontWeight: 700,
                                mb: 1,
                                position: 'relative',
                                zIndex: 1,
                                textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                            }}
                        >
                            Oops! Something Went Wrong
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                color: '#A0DCD7',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            We've hit a snag in the field
                        </Typography>
                    </Box>

                    {/* Content Section */}
                    <Box sx={{ padding: 4 }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#003C37',
                                mb: 3,
                                textAlign: 'center',
                                fontSize: '1.1rem',
                            }}
                        >
                            Don't worry! Just like a crop that needs a little care, we can get things growing again.
                        </Typography>

                        {/* Development Error Details */}
                        {process.env.NODE_ENV === 'development' && (
                            <Paper
                                sx={{
                                    p: 2,
                                    mb: 3,
                                    bgcolor: '#FFF3E0',
                                    border: '1px solid #FFB74D',
                                    borderRadius: 2,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        color: '#E65100',
                                        fontWeight: 600,
                                        mb: 1,
                                    }}
                                >
                                    🔧 Developer Info:
                                </Typography>
                                <Typography
                                    variant="caption"
                                    component="pre"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        color: '#5D4037',
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem',
                                    }}
                                >
                                    {error.message}
                                </Typography>
                            </Paper>
                        )}

                        {/* Action Buttons */}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 2,
                                flexWrap: 'wrap',
                                justifyContent: 'center',
                                mb: 3,
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<Refresh />}
                                onClick={resetErrorBoundary}
                                sx={{
                                    bgcolor: '#003C37',
                                    color: '#D5EE97',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    '&:hover': {
                                        bgcolor: '#14504B',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(0,60,55,0.3)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Try Again
                            </Button>

                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<Home />}
                                onClick={() => {
                                    window.location.href = '/';
                                }}
                                sx={{
                                    borderColor: '#003C37',
                                    color: '#003C37',
                                    fontWeight: 600,
                                    px: 4,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderColor: '#14504B',
                                        bgcolor: 'rgba(0,60,55,0.05)',
                                        borderWidth: 2,
                                        transform: 'translateY(-2px)',
                                    },
                                    transition: 'all 0.3s ease',
                                }}
                            >
                                Go Home
                            </Button>
                        </Box>

                        {/* Additional Help */}
                        <Box
                            sx={{
                                textAlign: 'center',
                                pt: 2,
                                borderTop: '1px solid #E0E0E0',
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#666',
                                    mb: 1,
                                }}
                            >
                                Still having trouble?
                            </Typography>
                            <Button
                                variant="text"
                                size="small"
                                onClick={() => {
                                    localStorage.clear();
                                    window.location.href = '/';
                                }}
                                sx={{
                                    color: '#003C37',
                                    textTransform: 'none',
                                    textDecoration: 'underline',
                                    '&:hover': {
                                        bgcolor: 'transparent',
                                        textDecoration: 'underline',
                                        color: '#14504B',
                                    },
                                }}
                            >
                                Clear cache and reload
                            </Button>
                        </Box>

                        {/* Support Message */}
                        <Box
                            sx={{
                                mt: 3,
                                p: 2,
                                bgcolor: '#E8F5E9',
                                borderRadius: 2,
                                textAlign: 'center',
                            }}
                        >
                            <Agriculture sx={{ color: '#4CAF50', fontSize: 32, mb: 1 }} />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#2E7D32',
                                    fontWeight: 500,
                                }}
                            >
                                If the problem persists, our support team is here to help you cultivate success!
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
}

// Main Error Boundary wrapper
function AppWithErrorBoundary({ children }) {
    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error, info) => {
                // Log error to console and server
                logErrorToServer(error, info);
            }}
            onReset={() => {
                // Reset app state on error boundary reset
                window.location.href = '/';
            }}
        >
            {children}
        </ErrorBoundary>
    );
}

export default AppWithErrorBoundary;
